import more_ic from '@/assets/icons/core/ic_core_more_options.svg?raw';
import { html, LitElement, PropertyValues, unsafeCSS } from "lit";
import { customElement, property, query, state } from "lit/decorators.js";
import { repeat } from "lit/directives/repeat.js";
import { unsafeHTML } from "lit/directives/unsafe-html.js";
import '../Badge/Badge';
import '../Buttons/IconButton/IconButton';
import { IconButtonSizeMap } from "../Buttons/IconButton/IconButton";
import '../Icon/Icon';
import '../Menu/Menu';
import { Menu } from "../Menu/Menu";
import '../MenuItem/MenuItem';
import styles from './ActionMenu.scss?inline';

export interface ActionMenuItem {
    ID: string;
    Icon: string;
    Color: string;
    Label: string;
    IsPrimary?: boolean;
    Badge?: string | number;
    onClick: (event: Event) => void;
}

@customElement('app-action-menu')
export class ActionMenu extends LitElement {
    static styles = [unsafeCSS(styles)];
    @property({ type: String, attribute: false }) k_iconSize: 'extra-small' | 'small' | 'medium' | 'large' | 'extra-large' = 'small';
    @property({ type: Array }) k_actions: ActionMenuItem[] = [];
    @query('#btn-more') btnOverflowActions !: HTMLElement;
    @query('app-menu') private menuOverflow  !: Menu;

    @state() private visibleCount: number = 0;

    private resizeObserver: ResizeObserver;

    connectedCallback(): void {
        super.connectedCallback();
        this.resizeObserver = new ResizeObserver((entries) => {
            const containerWidth = entries[0].contentRect.width;
            this.CalculateActionsOverflow(containerWidth);
        })
        this.resizeObserver.observe(this);
    }

    disconnectedCallback(): void {
        super.disconnectedCallback();
        this.resizeObserver.disconnect();
    }

    protected willUpdate(changedProperties: PropertyValues): void {
        if (changedProperties.has("k_actions")) {
            this.CalculateActionsOverflow(this.clientWidth);
        }
    }

    render() {
        const sliceDivider =
            this.visibleCount < this.k_actions.length && this.visibleCount != 0
                ? this.visibleCount - 1
                : this.visibleCount;
        const visibleActions = this.k_actions.slice(0, sliceDivider);
        const overflowActions = this.k_actions.slice(sliceDivider);

        return html`
            <div class='action-bar'>
                ${repeat(visibleActions, item => item.ID, item => this.RenderVisibleAction(item))}
                ${this.RenderOverflowActions(overflowActions)}
            </div>
        `
    }

    private RenderVisibleAction(itemMenu: ActionMenuItem) {
        const { Color = '', onClick, Label = undefined, Icon, Badge } = itemMenu;
        return html`
            <app-icon-button
                name=${itemMenu.ID}
                .k_size=${this.k_iconSize}
                .k_label=${Label}
                k_colorStyle='standard'
                style='--btn-theme-color: ${Color};'
                @click=${onClick}
            >
                <app-icon-dumb slot='icon' k_badge=${Badge}>${unsafeHTML(Icon)}</app-icon-dumb>
            </app-icon-button>
        `
    }

    private RenderOverflowActions(overflowActions: ActionMenuItem[]) {
        if (!overflowActions.length) return null;

        const hasBadgeInOverflow = overflowActions.some(
            item => item.Badge !== undefined && item.Badge !== '' && item.Badge !== 0
        );

        return html`
            <app-icon-button
                id='btn-more'
                .k_size=${this.k_iconSize}
                .k_label=${'Más opciones'}
                k_colorStyle='standard'
                style='color: var(--color-text)'
                @click=${(e: Event) => { this.menuOverflow.anchorElement = e.currentTarget as HTMLElement; this.menuOverflow.toggle() }}
            >
                <app-icon-dumb slot='icon' .k_isDot=${hasBadgeInOverflow}>${unsafeHTML(more_ic)}</app-icon-dumb>
            </app-icon-button>
            <app-menu>
                ${repeat(overflowActions, item => item.ID, item => this.RenderOverflowAction(item))}
            </app-menu>
        `
    }

    private RenderOverflowAction(itemMenu: ActionMenuItem) {
        const { Color = '', onClick, Icon, Label, Badge } = itemMenu;

        return html`
            <app-menu-item-dumb @click=${onClick}>
                <app-icon-dumb slot='start' style='height: 20px; width: 20px; color:${Color}'>${unsafeHTML(Icon)}</app-icon-dumb>
                ${Label}
                ${Badge !== undefined ? html`<app-badge slot='end' .k_value=${Badge}></app-badge>` : null}
            </app-menu-item-dumb>
        `
    }

    private CalculateActionsOverflow(availableWidth: number) {
        let maxItemsFit = Math.floor((availableWidth) / IconButtonSizeMap[this.k_iconSize]);
        if (maxItemsFit < 0) maxItemsFit = 0;
        if (maxItemsFit > this.k_actions.length) maxItemsFit = this.k_actions.length;

        this.visibleCount = maxItemsFit;
    }
}